import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createTextVNode as _createTextVNode, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden" }
const _hoisted_2 = { class: "lg:hidden mt-4 text-center" }
const _hoisted_3 = { class: "text-xs text-slate-500" }
const _hoisted_4 = { class: "font-semibold text-slate-700" }

import type { PaginacionFilterParams } from "@/utils/PaginacionFilterParams";
import { computed, ref } from "vue";
import ProductoTable from "../components/productoComponents/ProductoTable.vue";
import EmptyState from "../components/productoComponents/EmptyState.vue";
import ErrorState from "@/share/components/ErrorState.vue";
import FormularioProducto from "../components/productoComponents/FormularioProducto.vue";
import ModalPromocionProducto from "../components/productoComponents/ModalPromocionProducto.vue";
import { useProductosList, useProductoToggleDestacado } from "../composables/productoComposables";
import { useProductoCreate } from "../composables/productoComposables/useProductoCreate";
import {
  useProductoUpdate,
  useProductoDelete,
  useProductoToggleVisib,
} from "../composables/productoComposables";
import SkeletonTablas from "@/share/components/SkeletonTablas.vue";
import PaginationCompontens from "@/share/components/PaginationCompontens.vue";
import FiltrosComponents from "@/share/components/filtrosComponents.vue";
import AdminPageShell from "@/share/components/admin/AdminPageShell.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductosView',
  setup(__props) {

const params = ref<PaginacionFilterParams>({
  page: 1,
  limit: 10,
  isVisible: undefined,
  search: "",
});

const sortField = ref<
  | "nombreProducto"
  | "descripcionProducto"
  | "precioProducto"
  | "stockProducto"
  | "isVisible"
  | "isDestacado"
  | "createdAt"
  | null
>(null);
const sortDirection = ref<"asc" | "desc">("asc");

const isFormularioOpen = ref(false);
const productoEditId = ref<string | undefined>(undefined);
const promocionModalProductoId = ref<string | null>(null);
const isDelete = ref(false);

const {
  productos,
  total,
  totalPages,
  currentPage,
  isPending: isLoadingList,
  isError,
  error,
  refetch,
} = useProductosList(params);

const { mutate: crearProducto, isPending: isCreatingProducto } = useProductoCreate(() => {
  isFormularioOpen.value = false;
});
const { mutate: updateProducto, isPending: isUpdatingProducto } = useProductoUpdate(() => {
  isFormularioOpen.value = false;
  productoEditId.value = undefined;
});
const { mutate: updateProductoPromocion, isPending: isUpdatingPromocion } = useProductoUpdate(
  () => {
    promocionModalProductoId.value = null;
  }
);

const isSavingProducto = computed(() =>
  productoEditId.value ? isUpdatingProducto.value : isCreatingProducto.value
);
const { mutate: deleteProducto } = useProductoDelete(() => {
  isDelete.value = true;
  setTimeout(() => (isDelete.value = false), 100);
});
const { mutate: toggleVisibility } = useProductoToggleVisib();
const { mutate: toggleDestacado } = useProductoToggleDestacado();

const productosOrdenados = computed(() => {
  let data = [...productos.value];
  const field = sortField.value;

  if (field) {
    data.sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection.value === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection.value === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }
  return data;
});

// ðŸ§  Handlers
const handleSearch = (search: string) => {
  params.value.search = search;
  params.value.page = 1;
};

const handleVisibilityFilter = (visibility: number | undefined) => {
  params.value.isVisible = visibility;
  params.value.page = 1;
};

const handlePageChange = (page: number) => {
  params.value.page = page;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleLimitChange = (limit: number) => {
  params.value.limit = limit;
  params.value.page = 1;
};

const handleSort = (
  field:
    | "nombreProducto"
    | "descripcionProducto"
    | "precioProducto"
    | "stockProducto"
    | "isVisible"
    | "isDestacado"
    | "createdAt"
) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const handleOpenCreate = () => {
  productoEditId.value = undefined;
  isFormularioOpen.value = true;
};

const handleEditProducto = (id: string) => {
  productoEditId.value = id;
  isFormularioOpen.value = true;
};

const handleSaveProducto = (producto: FormData) => {
  crearProducto(producto);
};

const handleUpdateProducto = (id: string, formData: FormData) => {
  updateProducto({ idProducto: id, formData });
};

const handleCloseFormulario = () => {
  isFormularioOpen.value = false;
  productoEditId.value = undefined;
};

const handleDeleteProducto = (id: string) => {
  deleteProducto(id);
};

const handleToggleVisibility = async (id: string) => {
  toggleVisibility(id);
};
const handleToggleDestacado = async (id: string) => {
  toggleDestacado(id);
};

const handleOpenPromocionModal = (id: string) => {
  promocionModalProductoId.value = id;
};

const handleClosePromocionModal = () => {
  promocionModalProductoId.value = null;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminPageShell, {
    title: "Productos",
    description: "Administra tu catálogo de productos, precios, stock y visibilidad."
  }, {
    actions: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        onClick: handleOpenCreate,
        class: "w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("svg", {
          class: "w-5 h-5 flex-shrink-0",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 4v16m8-8H4"
          })
        ], -1),
        _createTextVNode(" Nuevo producto ", -1)
      ]))])
    ]),
    default: _withCtx(() => [
      _createVNode(FormularioProducto, {
        isOpen: isFormularioOpen.value,
        productoId: productoEditId.value,
        "is-saving": isSavingProducto.value,
        onClose: handleCloseFormulario,
        onSave: handleSaveProducto,
        onUpdate: handleUpdateProducto
      }, null, 8, ["isOpen", "productoId", "is-saving"]),
      _createVNode(ModalPromocionProducto, {
        open: !!promocionModalProductoId.value,
        "producto-id": promocionModalProductoId.value,
        "is-saving": _unref(isUpdatingPromocion),
        onClose: handleClosePromocionModal,
        onUpdate: _cache[0] || (_cache[0] = 
          (id, formData) =>
            _unref(updateProductoPromocion)({ idProducto: id, formData })
        )
      }, null, 8, ["open", "producto-id", "is-saving"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FiltrosComponents, {
          search: params.value.search,
          visibility: params.value.isVisible,
          limit: params.value.limit,
          total: _unref(total),
          onSearch: handleSearch,
          onVisibilityFilter: handleVisibilityFilter,
          onLimitChange: handleLimitChange,
          onRefresh: _unref(refetch)
        }, null, 8, ["search", "visibility", "limit", "total", "onRefresh"]),
        (_unref(isLoadingList))
          ? (_openBlock(), _createBlock(SkeletonTablas, { key: 0 }))
          : (_unref(isError))
            ? (_openBlock(), _createBlock(ErrorState, {
                key: 1,
                error: _unref(error),
                onRetry: _unref(refetch)
              }, null, 8, ["error", "onRetry"]))
            : (_unref(productos).length > 0)
              ? (_openBlock(), _createBlock(ProductoTable, {
                  key: 2,
                  productos: productosOrdenados.value,
                  "sort-field": sortField.value,
                  "sort-direction": sortDirection.value,
                  currentPage: _unref(currentPage),
                  pageSize: params.value.limit,
                  isDelete: isDelete.value,
                  onSort: handleSort,
                  onToggleVisibility: handleToggleVisibility,
                  onToggleDestacado: handleToggleDestacado,
                  onOpenPromocionModal: handleOpenPromocionModal,
                  onEditProducto: handleEditProducto,
                  onDeleteProducto: handleDeleteProducto
                }, null, 8, ["productos", "sort-field", "sort-direction", "currentPage", "pageSize", "isDelete"]))
              : (_openBlock(), _createBlock(EmptyState, { key: 3 })),
        (!_unref(isLoadingList) && !_unref(isError) && _unref(total) > 0)
          ? (_openBlock(), _createBlock(PaginationCompontens, {
              key: 4,
              "current-page": _unref(currentPage),
              "total-pages": _unref(totalPages),
              total: _unref(total),
              limit: params.value.limit!,
              onPageChange: handlePageChange
            }, null, 8, ["current-page", "total-pages", "total", "limit"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, [
          _cache[2] || (_cache[2] = _createTextVNode(" Total de productos: ", -1)),
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(total)), 1)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})